import { ref, watch } from 'vue'

export function useInfoBikeSearch({
  getEditor,
  getEditingData,
  setVehicle,
  beginLoading = () => {},
  endLoading = () => {},
  registryStore,
  garageStore,
  portalStore,
}) {
  const infoBikeSearchDisabled = ref(true)
  const showPulse = ref(false)
  const pulseCount = ref(1)
  const currentInfoBikeBrandCode = ref(null)
  const currentInfoBikeModelCode = ref(null)

  const extraData = ref({})

  watch(infoBikeSearchDisabled, (newVal, oldVal) => {
    if (oldVal === true && newVal === false) showPulse.value = true
  })

  const disableInfoBikeSearch = () => { infoBikeSearchDisabled.value = true }

  const getOwnerData = () => {
    const data = getEditingData()
    return {
      customer: data?.customer && {
        ...data.customer,
        address: data.customer.address && { ...data.customer.address },
      },
      customerId: data?.customer_id,
    }
  }

  const setVehiclePreservingOwner = (vehicle, ownerData) => setVehicle(vehicle, { preserveOwner: true, ...ownerData })

  const clearInfoBikeSearchFields = () => {
    getEditor('infoBike.brand')?.option('value', null)
    getEditor('infoBike.model')?.option('value', null)
    getEditor('infoBike.variant')?.option('value', null)
    getEditor('infoBike.infoBike')?.option('value', null)
  }

  const onPlateBlur = (customFunction = null) => {
    const data = getEditingData()
    const ownerData = getOwnerData()
    const plate = data?.infoBike?.plate_number
    if (plate && plate.length > 2) {
      getEditor('infoBike.brand')?.option('disabled', true)
      getEditor('infoBike.model')?.option('disabled', true)
      getEditor('infoBike.variant')?.option('disabled', true)
      clearInfoBikeSearchFields()
      const vehicle = registryStore.vehicles.find(i => i.plate_number === plate)
      if (vehicle && vehicle.infoBikeCodeFull) {
        infoBikeSearchDisabled.value = true
        if (typeof customFunction === 'function') {
          customFunction(vehicle.id)
        } else {
          setVehiclePreservingOwner(vehicle, ownerData)
        }
      } else {
        infoBikeSearchDisabled.value = false
        setVehiclePreservingOwner(null, ownerData)
      }
    } else if (plate && plate.length <= 2) {
      infoBikeSearchDisabled.value = true
      getEditor('infoBike.brand')?.option('disabled', false)
      getEditor('infoBike.model')?.option('disabled', false)
      getEditor('infoBike.variant')?.option('disabled', false)
    }
  }

  const infoBikeSearch = async(vehiclePrefix = 'vehicle.') => {
    const data = getEditingData()
    if (!data) return
    const ownerData = getOwnerData()

    beginLoading()
    infoBikeSearchDisabled.value = true
    try {
      const plate_number = data.infoBike?.plate_number
      const res = await registryStore.api.getInfoBikeSearchAsync(plate_number)
      if (res.data !== '') {
        await registryStore.fetchVehiclesAsync(true)
        const vehicle = registryStore.vehicles.find(i => i.plate_number === plate_number)
        if (vehicle) {
          setVehiclePreservingOwner(vehicle, ownerData)
          getEditor(`${vehiclePrefix}infoBikeCodeFull`)?.option('value', res.data['CodiceInfobikePRG'])
        } else {
          const supplierBrandId = registryStore.supplierBrands.find(i => i.infoBike_brand_code === res.data['CodiceMarca'])?.id ?? null
          const vehicleTypeId = portalStore.vehicleTypes.find(i => i.infoBike_vehicle_type_value === res.data['TipoVeicolo'])?.id ?? null
          getEditor(`${vehiclePrefix}supplier_brand_ref`)?.option('value', supplierBrandId)
          getEditor(`${vehiclePrefix}vehicle_type`)?.option('value', vehicleTypeId)
          getEditor(`${vehiclePrefix}plate_number`)?.option('value', plate_number)
          getEditor(`${vehiclePrefix}vin`)?.option('value', res.data['vin'])
          if (!getEditor(`${vehiclePrefix}vin`)) {
            if (data[`${vehiclePrefix}vin`]) {
              data[`${vehiclePrefix}vin`] = res.data['vin']
            } else {
              data.vehicle.vin = res.data['vin']
            }
          }
          getEditor(`${vehiclePrefix}engine_code`)?.option('value', res.data['engine_code'])
          getEditor(`${vehiclePrefix}infoBikeCodeFull`)?.option('value', res.data['CodiceInfobikePRG'])
          getEditor(`${vehiclePrefix}name`)?.option('value', res.data['DescrizioneCompleta'])
          if (res.data['vehicle_fuel_type'] === registryStore.vehicleFuelTypesEnum.ELECTRIC) {
            getEditor(`${vehiclePrefix}engine_size`)?.option('value', res.data['engine_power_kw'])
          } else {
            getEditor(`${vehiclePrefix}engine_size`)?.option('value', res.data['engine_size'])
          }
          getEditor(`${vehiclePrefix}vehicle_fuel_type`)?.option('value', res.data['vehicle_fuel_type'])
          getEditor(`${vehiclePrefix}engine_power_kw`)?.option('value', res.data['engine_power_kw'])
          getEditor(`${vehiclePrefix}co2_emissions`)?.option('value', res.data['co2_emissions'])
          getEditor(`${vehiclePrefix}emission_standard`)?.option('value', res.data['emission_standard'])
          getEditor(`${vehiclePrefix}transmission_type`)?.option('value', res.data['transmission_type'])
          extraData.value = {
            fuel_type: res.data['vehicle_fuel_type'],
            engine_code: res.data['engine_code'],
            engine_power_kw: res.data['engine_power_kw'],
            co2_emissions: res.data['co2_emissions'],
            emission_standard: res.data['emission_standard'],
            transmission_type: res.data['transmission_type'],
          }
        }
        const infoBikeBrand = registryStore.infoBikeBrands.find(i => i.ref === res.data['CodiceMarca'])
        if (infoBikeBrand) getEditor('infoBike.brand')?.option('value', infoBikeBrand.ref)
      } else {
        infoBikeSearchDisabled.value = false
      }
    } finally {
      endLoading()
      loadCreditsAsync()
    }
  }

  const loadCreditsAsync = async() => { await garageStore.fetchInfoBikePlateSearchCreditsAsync() }

  const onInfoBikeEditorPreparing = (e, editors, vehiclePrefix = 'vehicle.') => {
    if (e.parentType !== 'dataRow') return

    if (e.dataField === 'infoBike.plate_number') {
      e.editorOptions.onInitialized = (ev) => { editors['infoBike.plate_number'] = ev.component }
    }

    if (e.dataField === 'infoBike.brand') {
      e.editorOptions.onInitialized = (ev) => { editors['infoBike.brand'] = ev.component }
      e.editorOptions.searchEnabled = true
      e.editorOptions.searchExpr = ['code', 'name']
      e.editorOptions.disabled = !!e.row.data?.infoBike?.plate_number || !!e.row.data?.vehicle?.id
      const defaultValueChanged = e.editorOptions.onValueChanged
      e.editorOptions.onValueChanged = (args) => {
        defaultValueChanged(args)
        setVehicle(null)
        if (e.row.data?.vehicle) {
          e.row.data.vehicle.id = null
        }
        currentInfoBikeBrandCode.value = args.value
        getEditor('infoBike.model')?.option('value', null)
        getEditor('infoBike.model')?.option('disabled', !args.value)
        getEditor('infoBike.model')?.getDataSource()?.reload()
      }
    }
    if (e.dataField === 'infoBike.model') {
      e.editorOptions.onInitialized = (ev) => { editors['infoBike.model'] = ev.component }
      e.editorOptions.disabled = !currentInfoBikeBrandCode.value || !!e.row.data?.infoBike?.plate_number || !!e.row.data?.vehicle?.id
      const defaultValueChanged = e.editorOptions.onValueChanged
      e.editorOptions.onValueChanged = (args) => {
        defaultValueChanged(args)
        currentInfoBikeModelCode.value = args.value

        getEditor('infoBike.infoBike')?.option('value', null)
        getEditor(`${vehiclePrefix}name`)?.option('value', null)
        getEditor(`${vehiclePrefix}infoBikeCodeFull`)?.option('value', null)

        getEditor('infoBike.variant')?.option('value', null)
        getEditor('infoBike.variant')?.option('disabled', !args.value)
        getEditor('infoBike.variant')?.getDataSource()?.reload()
      }
    }
    if (e.dataField === 'infoBike.variant') {
      e.editorOptions.onInitialized = (ev) => { editors['infoBike.variant'] = ev.component }
      e.editorOptions.disabled = !currentInfoBikeModelCode.value || !!e.row.data?.infoBike?.plate_number || !!e.row.data?.vehicle?.id
      const defaultValueChanged = e.editorOptions.onValueChanged
      e.editorOptions.onValueChanged = (args) => {
        defaultValueChanged(args)
        if (args.value) {
          const selectedVariant = registryStore.infoBikeVariants[e.row.data.infoBike.model]?.find(i => i.ref === args.value)

          const infoBikeDescription = registryStore.infoBikeVariants[e.row.data.infoBike.model].find(i => i.ref === args.value)?.DescrizioneCompleta
          const supplierBrandId = registryStore.supplierBrands.find(i => i.infoBike_brand_code === e.row.data.infoBike.brand)?.id
          const vehicleTypeId = portalStore.vehicleTypes.find(i => i.infoBike_vehicle_type_value === selectedVariant?.TipoVeicolo)?.id ?? null
          getEditor('infoBike.infoBike')?.option('value', args.value)
          getEditor(`${vehiclePrefix}supplier_brand_ref`)?.option('value', supplierBrandId)
          getEditor(`${vehiclePrefix}name`)?.option('value', infoBikeDescription)
          getEditor(`${vehiclePrefix}infoBikeCodeFull`)?.option('value', args.value)
          getEditor(`${vehiclePrefix}vehicle_type`)?.option('value', vehicleTypeId)

          getEditor(`${vehiclePrefix}engine_size`)?.option('value', null)
          getEditor(`${vehiclePrefix}engine_power_kw`)?.option('value', null)
          getEditor(`${vehiclePrefix}plate`)?.option('value', null)
          getEditor(`${vehiclePrefix}vin`)?.option('value', null)
          getEditor(`${vehiclePrefix}vehicle_fuel_type`)?.option('value', null)
          getEditor(`${vehiclePrefix}emission_standard`)?.option('value', null)
          getEditor(`${vehiclePrefix}co2_emissions`)?.option('value', null)
          getEditor(`${vehiclePrefix}transmission_type`)?.option('value', null)
        }
      }
    }

    if (e.dataField === 'infoBike.infoBike') {
      e.editorOptions.onInitialized = (ev) => { editors['infoBike.infoBike'] = ev.component }
      e.editorOptions.disabled = true
    }
  }

  const infoBikeCascadeHandlers = (formRef) => ({
    onBrandChanged: (args) => {
      currentInfoBikeBrandCode.value = args.value
      formRef.value.formData.infoBike.model = null
      getEditor('infoBike.model')?.option('disabled', !args.value)
      getEditor('infoBike.model')?.getDataSource()?.reload()
    },
    onModelChanged: (args) => {
      currentInfoBikeModelCode.value = args.value
      formRef.value.formData.infoBike.variant = null
      getEditor('infoBike.variant')?.option('disabled', !args.value)
      getEditor('infoBike.variant')?.getDataSource()?.reload()
    },
    onVariantChanged: (args) => {
      if (!args.value) return
      currentInfoBikeModelCode.value = args.value
      const data = getEditingData()
      formRef.value.formData.infoBike.infoBike = args.value
      const infoBikeDescription = registryStore.infoBikeVariants[data.infoBike.model]
        .find(i => i.ref === args.value)?.DescrizioneCompleta
      const supplierBrandId = registryStore.supplierBrands
        .find(i => i.infoBike_brand_code === data.infoBike.brand)?.id
      formRef.value.formData.vehicle.supplier_brand_ref = supplierBrandId
      formRef.value.formData.vehicle.name = infoBikeDescription
      formRef.value.formData.vehicle.infoBikeCodeFull = args.value
    }
  })

  const infoBikeFormGroup = (i18n, garageStoreInstance, colSpan = 3) => ({
    itemType: 'group',
    caption: i18n.t('common.infoBikeSearch', { credits: garageStoreInstance.infoBikePlateSearchCredit?.credits || 0 }),
    name: 'mdi-cloud-search',
    captionTemplate: 'vehicle-infoBike-search-caption-template',
    validationGroup: 'infoBikeSearchGroup',
    colSpan: colSpan,
    colCount: 8,
    items: [
      { dataField: 'infoBike.plate_number', colSpan: 2 },
      { dataField: 'infoBike.brand', label: { text: i18n.t('common.brandAlt') } },
      { dataField: 'infoBike.model' },
      { dataField: 'infoBike.variant', editorOptions: { maxLength: 17 }, colSpan: 2 },
      { dataField: 'infoBike.infoBike', colSpan: 2 },
    ]
  })

  return {
    infoBikeSearchDisabled, showPulse, pulseCount,
    currentInfoBikeBrandCode, currentInfoBikeModelCode, extraData,
    disableInfoBikeSearch, onPlateBlur, infoBikeSearch, loadCreditsAsync,
    onInfoBikeEditorPreparing, infoBikeCascadeHandlers, infoBikeFormGroup,
  }
}
